'use client';

import { getPublicSupergraphUrl } from '@cc-shopping/data-access/graphql-client';
import { env } from '@cc-shopping/platform-env';
import { getDeviceIdWithoutHmac } from '@cc-shopping/utils/analytics/getDeviceId';
import { datadogRum, type RumEvent } from '@datadog/browser-rum';
import { useEffect, useRef } from 'react';
import { deviceType } from 'react-device-detect';
import { getBotCookies } from 'src/shared/utils/cookies';
const URLS_TO_SOURCE = {
  'cdn/apps/prod/global-markup/': 'global-markup',
  'cdn/apps/prod/compass/': 'compass'
};
const DATADOG_APP_ID = '1312b572-8935-44c8-a39c-198af0749581';
export function DataDogProvider() {
  const {
    current: deviceId
  } = useRef(getDeviceIdWithoutHmac());
  useEffect(() => {
    // Prevent double initialization - check if SDK is already loaded
    if (datadogRum.getInternalContext()) {
      return;
    }
    const {
      isBot,
      isVerifiedBot,
      botScore
    } = getBotCookies();
    if (env.NEXT_PUBLIC_DD_CLIENT_TOKEN) {
      const publicSupergraphUrl = getPublicSupergraphUrl();
      datadogRum.init({
        applicationId: DATADOG_APP_ID,
        clientToken: env.NEXT_PUBLIC_DD_CLIENT_TOKEN,
        site: 'datadoghq.com',
        enableExperimentalFeatures: ['feature_flags'],
        service: 'cc-shopping-client',
        sessionSampleRate: 100,
        sessionReplaySampleRate: 5,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        traceSampleRate: 100,
        defaultPrivacyLevel: 'mask-user-input',
        env: env.NEXT_PUBLIC_DEPLOYABLE_ENV,
        version: env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
        beforeSend: (event: RumEvent) => {
          if (event.type === 'error') {
            Object.entries(URLS_TO_SOURCE).map(([key, value]) => {
              if (event.error.stack && event.error.stack.includes(key)) {
                event.context = {
                  ...event.context,
                  nw_source: value
                };
              }
            });
            if (event.error.resource?.url) {
              const url = new URL(event.error.resource.url);
              if (['clarity.ms', 'tabloo', 'tiktok.com'].some(target => url.hostname.includes(target))) {
                return false;
              }
            }
            // Ignores Script error caused by 3rd party scripts
            if (event.error.message.includes('Script error') || event.error.message.includes('[Veritonic]') || event.error.message.includes('Failed to create PluginFactory')) {
              return false;
            }
            // Ignores GTM errors if within the stack trace
            if (event.error.stack?.includes('googletagmanager')) {
              return false;
            }

            // Handle ChunkLoadError
            if (event.error.type === 'ChunkLoadError') {
              const message: string = event.error.message;
              try {
                const urls = message.match(/https?:\/\/[^\s]+/);
                const dplURL = urls?.find(url_0 => url_0.includes('dpl='));
                if (dplURL) {
                  const failingDpl = new URL(dplURL).searchParams.get('dpl');
                  const currentDpl = env.NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID;
                  if (failingDpl && currentDpl && failingDpl !== currentDpl) {
                    event.error.message = `[Stale dpl] ${event.error.message}`;
                    event.context = {
                      ...event.context,
                      current_dpl: currentDpl,
                      chunk_error_reason: 'stale_dpl'
                    };
                    return true;
                  }
                }
              } catch {
                // malformed URL or no URL, leave event unchanged
              }
            }
          }
          return true;
        },
        allowedTracingUrls: [/https:\/\/cc-shopping-client-pr-\d+(-[a-z0-9-]+)?\.vercel\.stage\.nerdwallet\.biz\//, (url_1: string) => url_1.startsWith('https://localnerd.stage.nerdwallet.biz'), (url_2: string) => url_2.startsWith(env.NEXT_PUBLIC_NW_URL), (url_3: string) => url_3.startsWith(env.NEXT_PUBLIC_ABSMARTLY_URL), (url_4: string) => url_4.startsWith('https://siteusage.nerdwallet.com/v1'), (url_5: string) => url_5.startsWith('https://6wdju54t87.execute-api.us-east-1.amazonaws.com'), (url_6: string) => url_6.startsWith('https://hw3rs5zcr9.execute-api.us-east-1.amazonaws.com'), (url_7: string) => url_7.startsWith(publicSupergraphUrl)].map(matcher => ({
          match: matcher,
          propagatorTypes: ['tracecontext', 'datadog']
        }))
      });
      datadogRum.startSessionReplayRecording();
      datadogRum.setUserProperty('device_id', deviceId);
      datadogRum.setUserProperty('device_type', deviceType);
      datadogRum.setUserProperty('is_bot', isBot);
      datadogRum.setUserProperty('is_verified_bot', isVerifiedBot);
      datadogRum.setUserProperty('bot_score', botScore);
    }
  }, []);
  return null;
}